/** Tab „Benachrichtigungen": Web-Push, Desktop-Benachrichtigungen, Töne. */
import { useEffect, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { pushApi } from '../../lib/endpoints';
import { disablePush, enablePush, getPushState, type PushState } from '../../lib/pwa';
import { playNotificationSound } from '../../lib/sound';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { patchSettings } from './useSettings';

const PUSH_TEXT: Record<PushState, string> = {
  unsupported: 'Dieser Browser unterstützt keine Push-Benachrichtigungen.',
  unavailable: 'Push ist auf diesem Server nicht eingerichtet.',
  denied: 'Benachrichtigungen sind im Browser blockiert. Erlaube sie in den Website-Einstellungen.',
  enabled: 'Push ist auf diesem Gerät aktiv – du wirst auch bei geschlossener App benachrichtigt.',
  disabled: 'Push ist auf diesem Gerät aus.',
};

export function NotificationsTab() {
  const settings = useAppStore((s) => s.settings);
  const [push, setPush] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    void getPushState().then((s) => alive && setPush(s));
    return () => {
      alive = false;
    };
  }, []);
  if (!settings) return null;

  const run = async (fn: () => Promise<PushState>) => {
    setBusy(true);
    try {
      const s = await fn();
      setPush(s);
      if (s === 'denied') toast.error('Benachrichtigungen blockiert', PUSH_TEXT.denied);
      if (s === 'unavailable') toast.info(PUSH_TEXT.unavailable);
    } catch (e) {
      toast.error('Push konnte nicht geändert werden', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const setDesktop = async (on: boolean) => {
    if (on && 'Notification' in window && Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') {
        toast.error('Benachrichtigungen nicht erlaubt');
        return;
      }
    }
    await patchSettings({ notifyDesktop: on });
  };

  return (
    <div className="lf-settings-sections">
      <section className="lf-settings-card stack" aria-labelledby="push-heading" data-testid="push-section">
        <h2 id="push-heading">Push-Benachrichtigungen</h2>
        <p className="small muted" role="status" data-testid="push-status" data-state={push ?? 'loading'}>
          {push ? PUSH_TEXT[push] : 'Prüfe …'}
        </p>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {push === 'enabled' ? (
            <>
              <button type="button" className="btn" onClick={() => void run(disablePush)} disabled={busy} data-testid="push-disable">
                Push deaktivieren
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void pushApi.test().then(() => toast.info('Test-Benachrichtigung gesendet'), (e: unknown) => toast.error('Test fehlgeschlagen', errorMessage(e)))}
                data-testid="push-test"
              >
                Test senden
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void run(enablePush)}
              disabled={busy || push === 'unsupported' || push === 'denied' || push === null}
              data-testid="push-enable"
            >
              Push aktivieren
            </button>
          )}
        </div>
      </section>
      <section className="lf-settings-card stack" aria-labelledby="inapp-heading">
        <h2 id="inapp-heading">In der App</h2>
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={settings.notifyDesktop} onChange={(e) => void setDesktop(e.target.checked)} data-testid="notify-desktop" />
          <span>
            Desktop-Benachrichtigungen, wenn die App im Hintergrund offen ist
          </span>
        </label>
        <label className="switch-row">
          <input type="checkbox" className="switch" checked={settings.notifySound} onChange={(e) => void patchSettings({ notifySound: e.target.checked })} data-testid="notify-sound" />
          <span>Benachrichtigungstöne abspielen</span>
        </label>
        <div className="row">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => playNotificationSound()} data-testid="notify-sound-test">
            Ton anhören
          </button>
        </div>
        <p className="small muted">Stufen je Server oder Kanal (alle Nachrichten, nur Erwähnungen, nichts) stellst du im Kontextmenü des Servers bzw. Kanals ein.</p>
      </section>
    </div>
  );
}
